/**
 * EditAccountPageViewModel - Account Editing Form Management
 * 
 * Manages complete account editing functionality for PageContentEditAccount component
 * Handles profile data, form validation, account updates, and user actions
 * 
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import type {
  IPlatformServices,
  IEditAccountPageService,
  AccountFormData
} from '../contracts'
import { safeGetLogger } from '@restaurant/logging'


// Page content props interface (avoiding @restaurant/core React Native dependencies)
interface PageContentEditAccountProps {
  value?: AccountFormData
  onChange?: (fieldName: keyof AccountFormData, fieldValue: unknown, allValues: AccountFormData) => void
  disabled?: boolean
  fieldConfig?: {
    fullName?: {
      error?: string
      helperText?: string
      readOnly?: boolean
    }
    emailAddress?: {
      error?: string
      helperText?: string
      readOnly?: boolean
    }
    phoneNumber?: {
      error?: string
      helperText?: string
      readOnly?: boolean
    }
    birthday?: {
      error?: string
      helperText?: string
      readOnly?: boolean
    }
  }
  onSignOut?: () => void
  onDeleteAccount?: () => void
  onBirthdayClick?: () => void
  onSubmit?: () => void
  hasChanges?: boolean
  testID?: string
  className?: string
  style?: React.CSSProperties
  accessibilityLabel?: string
}

// ViewModel dependencies interface
export interface EditAccountPageViewModelDependencies {
  editAccountPageService?: IEditAccountPageService
}

// ViewModel state interface
export interface EditAccountPageViewModelState {
  // Form data
  formData: AccountFormData
  originalData: AccountFormData
  
  // Loading states
  loading: boolean
  saving: boolean
  validating: boolean
  
  // Form state
  hasChanges: boolean
  isValid: boolean
  errors: Partial<Record<keyof AccountFormData, string>>
  
  // Error handling
  error: string | null
  
}

// ViewModel actions interface
export interface EditAccountPageViewModelActions {
  // Form management
  handleFieldChange: (field: keyof AccountFormData, value: string) => void
  resetForm: () => void
  saveProfile: () => Promise<void>
  
  // Validation
  validateForm: () => Promise<void>
  clearError: () => void
  setError: (error: string) => void
  
  // User interactions
  handleSignOut: () => Promise<void>
  handleDeleteAccount: () => Promise<void>
  
  // State management
  setLoading: (loading: boolean) => void
  setSaving: (saving: boolean) => void
  
  // Component integration
  getPageContentEditAccountProps: () => Partial<PageContentEditAccountProps>
}

// Combined ViewModel interface
export interface EditAccountPageViewModel extends EditAccountPageViewModelState, EditAccountPageViewModelActions {}

/**
 * Platform-agnostic EditAccountPageViewModel factory
 * Uses dependency injection for platform-specific services
 * Returns a hook function that provides the ViewModel
 */
export const createEditAccountPageViewModel = (
  services: IPlatformServices,
  dependencies: EditAccountPageViewModelDependencies = {}
) => {
  return (): EditAccountPageViewModel => {
    // Dependencies
    const editAccountPageService = dependencies.editAccountPageService

    // Refs for cleanup and mount tracking
    const mountedRef = useRef(true)

    // State
    const [formData, setFormData] = useState<AccountFormData>({
    fullName: '',
    emailAddress: '',
    phoneNumber: '',
    birthday: '',
  })
  
  const [originalData, setOriginalData] = useState<AccountFormData>({
    fullName: '',
    emailAddress: '',
    phoneNumber: '',
    birthday: '',
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [validating, setValidating] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof AccountFormData, string>>>({})
  const [error, setError] = useState<string | null>(null)

  // Refs for debouncing
  const validationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Computed properties
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData)
  }, [formData, originalData])

  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0 &&
           (formData.fullName ?? '').trim() !== '' &&
           (formData.emailAddress ?? '').trim() !== ''
  }, [errors, formData])

  // Validation function (must be defined before useEffect that uses it)
  const validateFormData = useCallback(async (data: AccountFormData) => {
    if (!editAccountPageService) return

    try {
      setValidating(true)
      const result = await editAccountPageService.validateProfileData(data)
      if (mountedRef.current) {
        setErrors(result.errors)
      }
    } catch (err) {
      safeGetLogger()?.error('Validation failed', { domain: 'auth', error: String(err) })
    } finally {
      if (mountedRef.current) {
        setValidating(false)
      }
    }
  }, [editAccountPageService])

  // Load user profile on mount
  useEffect(() => {
    // Reset mountedRef on mount (fixes React Strict Mode double-render)
    mountedRef.current = true

    const loadProfile = async () => {
      if (!editAccountPageService) {
        if (mountedRef.current) {
          setError('Edit account service not available')
          setLoading(false)
        }
        return
      }

      try {
        setLoading(true)
        const userProfile = await editAccountPageService.getCurrentUserProfile()

        if (mountedRef.current && userProfile) {
          const accountData: AccountFormData = {
            fullName: `${userProfile.firstName} ${userProfile.lastName}`.trim(),
            emailAddress: userProfile.email,
            phoneNumber: (() => {
              // Strip country code for display ("+16264834818" → "6264834818")
              const raw = (userProfile.phoneNumber || '').replace(/\D/g, '')
              return raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw
            })(),
            birthday: (() => {
              const raw = userProfile.birthday || ''
              // Convert YYYY-MM-DD to "Jan 21" display format
              if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
                const [, m = '', d = ''] = raw.split('-')
                return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`
              }
              return raw
            })(),
          }

          setFormData(accountData)
          setOriginalData(accountData)

          // Initial validation
          await validateFormData(accountData)
        }
      } catch (err) {
        if (mountedRef.current) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load profile'
          setError(errorMessage)
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false)
        }
      }
    }

    loadProfile()

    return () => {
      mountedRef.current = false
    }
  }, [editAccountPageService, validateFormData])

  // Debounced validation
  const debouncedValidation = useCallback((data: AccountFormData) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    
    validationTimeoutRef.current = setTimeout(() => {
      validateFormData(data)
    }, 300)
  }, [validateFormData])

  // Actions
  const handleFieldChange = useCallback((field: keyof AccountFormData, value: string) => {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)
    
    // Clear specific field error
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
    
    // Trigger validation
    debouncedValidation(newFormData)
  }, [formData, errors, debouncedValidation])

  const resetForm = useCallback(() => {
    setFormData(originalData)
    setErrors({})
    setError(null)
  }, [originalData])

  const saveProfile = useCallback(async () => {
    if (!editAccountPageService) {
      setError('Edit account service not available')
      return
    }

    if (!isValid) {
      services.platformService.showToast({
        message: 'Please fix validation errors',
        type: 'error',
      })
      return
    }

    try {
      setSaving(true)
      await editAccountPageService.updateUserProfile(formData)

      // Update original data to reflect saved state
      setOriginalData(formData)
      setError(null)
      
      services.platformService.showToast({
        message: 'Profile updated successfully',
        type: 'success',
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile'
      setError(errorMessage)
      services.platformService.showToast({
        message: errorMessage,
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }, [editAccountPageService, isValid, formData, services.platformService])

  const validateForm = useCallback(async () => {
    await validateFormData(formData)
  }, [validateFormData, formData])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const handleSignOut = useCallback(async () => {
    if (!editAccountPageService) return

    try {
      const confirmed = await services.platformService.showConfirm({
        title: 'Sign Out',
        message: 'Are you sure you want to sign out?',
        confirmText: 'Sign Out',
        cancelText: 'Cancel',
      })

      if (confirmed) {
        await editAccountPageService.signOutUser()
        services.navigationService.navigateToHome()
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign out failed'
      setError(errorMessage)
      services.platformService.showToast({
        message: 'Failed to sign out',
        type: 'error',
      })
    }
  }, [editAccountPageService, services.platformService, services.navigationService])

  const handleDeleteAccount = useCallback(async () => {
    if (!editAccountPageService) return

    try {
      // Check for unsaved changes
      if (hasChanges) {
        const proceedWithUnsaved = await services.platformService.showConfirm({
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Are you sure you want to continue?',
          confirmText: 'Continue',
          cancelText: 'Cancel',
        })

        if (!proceedWithUnsaved) return
      }

      // Final confirmation for account deletion
      const confirmed = await services.platformService.showConfirm({
        title: 'Delete Account',
        message: 'Are you sure you want to permanently delete your account? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        destructive: true,
      })

      if (confirmed) {
        await editAccountPageService.deleteUserAccount()
        services.navigationService.navigateToHome()
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Account deletion failed'
      setError(errorMessage)
      services.platformService.showToast({
        message: 'Failed to delete account',
        type: 'error',
      })
    }
  }, [editAccountPageService, services.platformService, hasChanges, services.navigationService])

  // Component integration
  const getPageContentEditAccountProps = useCallback((): Partial<PageContentEditAccountProps> => {
    return {
      value: formData,
      onChange: (fieldName, fieldValue, _allValues) => {
        handleFieldChange(fieldName, fieldValue as string)
      },
      disabled: loading || saving,
      fieldConfig: {
        fullName: {
          error: errors.fullName,
          helperText: errors.fullName ? undefined : 'Enter your full name',
        },
        emailAddress: {
          error: errors.emailAddress,
          helperText: errors.emailAddress ? undefined : 'Enter your email address',
        },
        phoneNumber: {
          error: errors.phoneNumber,
          helperText: 'Contact support to change your phone number',
          readOnly: true,
        },
        // Birthday is set-once: locked when the profile already has one,
        // tappable (field group's internal calendar) when it doesn't. The
        // unlocked config must OMIT the readOnly key entirely — spreading
        // readOnly: false onto the web TextField would override its hardcoded
        // readOnly attr and let the user type free text.
        birthday: originalData.birthday
          ? {
              error: errors.birthday,
              helperText: "Birthday can't be changed",
              readOnly: true,
            }
          : {
              error: errors.birthday,
              helperText: errors.birthday ? undefined : 'Tap to select your birthday',
            },
      },
      onSignOut: handleSignOut,
      onDeleteAccount: handleDeleteAccount,
      onSubmit: saveProfile,
      hasChanges,
      testID: 'edit-account-page',
    }
  }, [
    formData,
    originalData.birthday,
    loading,
    saving,
    errors,
    handleFieldChange,
    handleSignOut,
    handleDeleteAccount,
    saveProfile,
    hasChanges,
  ])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  return {
    // State
    formData,
    originalData,
    loading,
    saving,
    validating,
    hasChanges,
    isValid,
    errors,
    error,

    // Actions
    handleFieldChange,
    resetForm,
    saveProfile,
    validateForm,
    clearError,
    setError,
    handleSignOut,
    handleDeleteAccount,
    setLoading,
    setSaving,
    getPageContentEditAccountProps,
  }
  }
}